import React, { useEffect, useMemo, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { adminAPI, raBillTemplatesAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../utils/notify';

const EMPTY_FORM = {
  name: '',
  code: '',
  status: 'Active',
  description: '',
};

const EMPTY_RULE_FORM = {
  financial_year: '',
  security_deposit_pct: 2.5,
  gst_pct: 2,
  income_tax_pct: 2,
  labour_cess_pct: 1,
  taxable_base_method: 'CPWD_5B',
  recovery_method: 'CPWD_FIXED',
  extra_fields: '',
};

const formatFinancialYearLabelIN = (fyValue) => {
  const raw = String(fyValue || '').trim();
  const m = raw.match(/^(\d{4})-(\d{2})$/);
  if (!m) return raw || '-';
  const start = Number(m[1]);
  const end = start + 1;
  return `FY ${m[1]}-${m[2]} (01 Apr ${start} - 31 Mar ${end})`;
};

const templateOptionLabel = (row) => {
  if (!row) return '';
  const status = row.status ? ` · ${row.status}` : '';
  return `${row.name || row.code} (${row.code} v${row.version}${status})`;
};

const Departments = ({ onNavigate } = {}) => {
  const [dataReady, setDataReady] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rules, setRules] = useState([]);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE_FORM);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  /** projectId -> template id string ('' = inherit department default) */
  const [projectTemplateOverrides, setProjectTemplateOverrides] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const [deptResp, tplResp, projectsResp] = await Promise.all([
          adminAPI.getDepartments(),
          raBillTemplatesAPI.list().catch(() => ({ data: [] })),
          adminAPI.getProjects().catch(() => ({ data: [] })),
        ]);
        const { rows } = unwrapListResponse(deptResp);
        const mapped = safeArray(rows).map((row) => ({
          id: row.id,
          name: row.name || '',
          code: row.code || '',
          status: row.status || 'Active',
          description: row.description || '',
          metadata: row.metadata || {},
        }));
        mapped.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setDepartments(mapped);
        const tplRows = tplResp?.data?.data || tplResp?.data || [];
        setTemplates(safeArray(tplRows));
        const projectRows = unwrapListResponse(projectsResp).rows;
        setAllProjects(safeArray(projectRows).map((row) => ({
          id: row.id,
          name: row.name || '',
          code: row.code || row.agreementPrefix || '',
          departmentId: row.departmentId ?? row.department_id ?? null,
          metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : {},
        })));
      } catch (e) {
        console.warn('Departments API unavailable:', e?.message || e);
        setDepartments([]);
      } finally {
        setDataReady(true);
      }
    };
    load();
  }, []);

  const templateById = useMemo(() => {
    const map = new Map();
    templates.forEach((row) => map.set(Number(row.id), row));
    return map;
  }, [templates]);

  const assignedTemplateFor = (dept) => {
    const rawId = dept?.metadata?.ra_bill_template_id;
    if (rawId) return templateById.get(Number(rawId)) || null;
    return templates.find((t) => Number(t.departmentId) === Number(dept?.id)) || null;
  };

  const selectedTemplate = selectedTemplateId ? templateById.get(Number(selectedTemplateId)) : null;

  const departmentProjects = useMemo(() => {
    if (!editing?.id) return [];
    return allProjects
      .filter((p) => Number(p.departmentId) === Number(editing.id))
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [allProjects, editing?.id]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Department name is required.';
    if (!form.code.trim()) errs.code = 'Department code is required.';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setRules([]);
    setRuleForm(EMPTY_RULE_FORM);
    setSelectedTemplateId('');
    setProjectTemplateOverrides({});
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      code: row.code || '',
      status: row.status || 'Active',
      description: row.description || '',
    });
    const fyRules = row?.metadata?.ra_rules_by_financial_year || {};
    const mappedRules = Object.entries(fyRules).map(([financialYear, rule]) => ({
      financial_year: financialYear,
      security_deposit_pct: Number(rule?.security_deposit_pct ?? 2.5),
      gst_pct: Number(rule?.gst_pct ?? 2),
      income_tax_pct: Number(rule?.income_tax_pct ?? 2),
      labour_cess_pct: Number(rule?.labour_cess_pct ?? 1),
      taxable_base_method: rule?.taxable_base_method || 'CPWD_5B',
      recovery_method: rule?.recovery_method || 'CPWD_FIXED',
      extra_fields: Array.isArray(rule?.extra_fields) ? rule.extra_fields.join(', ') : '',
    }));
    mappedRules.sort((a, b) => String(a.financial_year).localeCompare(String(b.financial_year)));
    setRules(mappedRules);
    setRuleForm(EMPTY_RULE_FORM);
    const assigned = assignedTemplateFor(row);
    setSelectedTemplateId(assigned?.id ? String(assigned.id) : '');
    const overrides = {};
    allProjects
      .filter((p) => Number(p.departmentId) === Number(row.id))
      .forEach((p) => {
        const raw = p.metadata?.ra_bill_template_id;
        overrides[p.id] = raw ? String(raw) : '';
      });
    setProjectTemplateOverrides(overrides);
    setFormErrors({});
    setShowModal(true);
  };

  const handleAddOrUpdateRule = () => {
    if (!ruleForm.financial_year.trim()) {
      notifyError('Financial year is required for rule.');
      return;
    }
    const nextRule = {
      financial_year: ruleForm.financial_year.trim(),
      security_deposit_pct: Number(ruleForm.security_deposit_pct || 0),
      gst_pct: Number(ruleForm.gst_pct || 0),
      income_tax_pct: Number(ruleForm.income_tax_pct || 0),
      labour_cess_pct: Number(ruleForm.labour_cess_pct || 0),
      taxable_base_method: ruleForm.taxable_base_method || 'CPWD_5B',
      recovery_method: ruleForm.recovery_method || 'CPWD_FIXED',
      extra_fields: ruleForm.extra_fields || '',
    };
    setRules((prev) => {
      const withoutCurrent = prev.filter((item) => item.financial_year !== nextRule.financial_year);
      const next = [...withoutCurrent, nextRule];
      next.sort((a, b) => String(a.financial_year).localeCompare(String(b.financial_year)));
      return next;
    });
    setRuleForm(EMPTY_RULE_FORM);
  };

  const openRaBillTemplates = (templateId) => {
    if (templateId) sessionStorage.setItem('openRaBillTemplateId', String(templateId));
    if (typeof onNavigate === 'function') onNavigate('admin-ra-templates');
    else notifyError('Open RA Bill Templates from the Admin Panel menu to edit fields and formulas.');
  };

  const persistProjectTemplateOverrides = async () => {
    if (!editing?.id) return;
    const updates = [];
    for (const project of departmentProjects) {
      const nextRaw = projectTemplateOverrides[project.id] ?? '';
      const nextId = nextRaw ? Number(nextRaw) : null;
      const prevRaw = project.metadata?.ra_bill_template_id;
      const prevId = prevRaw ? Number(prevRaw) : null;
      if (nextId === prevId || (!nextId && !prevId)) continue;
      updates.push(
        adminAPI.updateProject(project.id, {
          metadata: {
            ...(project.metadata || {}),
            ra_bill_template_id: nextId,
          },
        }).then((res) => {
          const updated = res?.data || {};
          return {
            id: project.id,
            metadata: updated.metadata && typeof updated.metadata === 'object'
              ? updated.metadata
              : { ...(project.metadata || {}), ra_bill_template_id: nextId },
          };
        }),
      );
    }
    if (!updates.length) return;
    const results = await Promise.all(updates);
    setAllProjects((prev) => prev.map((p) => {
      const hit = results.find((r) => Number(r.id) === Number(p.id));
      return hit ? { ...p, metadata: hit.metadata } : p;
    }));
  };

  const handleSave = async () => {
    if (!validate()) return;
    const isUpdate = !!editing?.id;
    if (!(await confirmDialog(
      isUpdate ? 'Update this department master?' : 'Create this department master?',
      isUpdate ? 'Update Department' : 'Create Department',
    ))) return;
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      status: form.status,
      description: form.description.trim() || null,
      metadata: {
        ...(editing?.metadata || {}),
        ra_rules_by_financial_year: rules.reduce((acc, item) => {
          const extraFields = String(item.extra_fields || '')
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);
          acc[item.financial_year] = {
            security_deposit_pct: Number(item.security_deposit_pct || 0),
            gst_pct: Number(item.gst_pct || 0),
            income_tax_pct: Number(item.income_tax_pct || 0),
            labour_cess_pct: Number(item.labour_cess_pct || 0),
            taxable_base_method: item.taxable_base_method || 'CPWD_5B',
            recovery_method: item.recovery_method || 'CPWD_FIXED',
            extra_fields: extraFields,
          };
          return acc;
        }, {}),
        ra_bill_template_id: selectedTemplateId ? Number(selectedTemplateId) : null,
      },
    };
    try {
      if (isUpdate) {
        const res = await adminAPI.updateDepartment(editing.id, payload);
        const updated = res?.data;
        setDepartments((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
        await persistProjectTemplateOverrides();
        notifySuccess('Department updated.');
      } else {
        const res = await adminAPI.createDepartment(payload);
        const created = res?.data;
        setDepartments((prev) => {
          const next = [{ ...created }, ...prev];
          next.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          return next;
        });
        notifySuccess('Department created.');
      }
      setShowModal(false);
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Unable to save department.');
    }
  };

  const handleDelete = async () => {
    if (!editing?.id) return;
    if (!(await confirmDialog(
      `Remove department "${editing.name}"?`,
      'Remove Department',
    ))) return;
    try {
      await adminAPI.deleteDepartment(editing.id);
      setDepartments((prev) => prev.filter((item) => item.id !== editing.id));
      setShowModal(false);
      notifySuccess('Department removed.');
    } catch (e) {
      notifyError(e?.response?.data?.message || 'Unable to remove department.');
    }
  };

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Department Master</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>Department Master <span className="count-badge">{departments.length}</span></h2>
        <button className="btn-primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Department
        </button>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Status</th>
              <th>RA Bill Template</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>
                  No departments configured.
                </td>
              </tr>
            ) : departments.map((row) => {
              const tpl = assignedTemplateFor(row);
              return (
                <tr key={row.id} className="clickable" onClick={() => openEdit(row)}>
                  <td style={{ fontWeight: 600 }}>{row.name}</td>
                  <td>{row.code}</td>
                  <td>{row.status}</td>
                  <td>{tpl ? `${tpl.name} (v${tpl.version})` : '—'}</td>
                  <td style={{ maxWidth: 360, whiteSpace: 'normal' }}>{row.description || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="panel page-editor-panel" style={{ marginTop: 16 }}>
          <div className="section-header">
            <h2>{editing ? `Edit Department: ${editing.name}` : 'Add Department'}</h2>
            <button className="btn-secondary" onClick={() => setShowModal(false)}>Close</button>
          </div>
          <div>
              <div className="form-row">
                <div className="form-group">
                  <label>Name <span className="req">*</span></label>
                  <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="CPWD / NBCC / Railways" />
                  {formErrors.name && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.name}</p>}
                </div>
                <div className="form-group">
                  <label>Code <span className="req">*</span></label>
                  <input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))} placeholder="CPWD" />
                  {formErrors.code && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{formErrors.code}</p>}
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Department specific notes..." />
              </div>

              <h3 className="page-section-title">RA Bill Template</h3>
              <p className="page-section-hint">
                Assign a template here. Field layout and formulas are edited only in <strong>RA Bill Templates</strong>.
              </p>
              <div className="form-row">
                <div className="form-group" style={{ flex: '1 1 360px' }}>
                  <label>Assigned template</label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                  >
                    <option value="">None</option>
                    {templates.map((row) => (
                      <option key={row.id} value={String(row.id)}>{templateOptionLabel(row)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={!selectedTemplate}
                    onClick={() => openRaBillTemplates(selectedTemplate?.id)}
                  >
                    Open in RA Bill Templates
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => openRaBillTemplates(null)}
                  >
                    Go to RA Bill Templates
                  </button>
                </div>
              </div>
              {selectedTemplate ? (
                <div className="page-readonly-card">
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{selectedTemplate.name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>
                    Code {selectedTemplate.code} · v{selectedTemplate.version} · {selectedTemplate.status || 'DRAFT'} · {selectedTemplate.calculationEngine || '—'}
                    {selectedTemplate._count?.fields != null ? ` · ${selectedTemplate._count.fields} fields` : ''}
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                    Read-only here. Use RA Bill Templates to change sections, fields, or formulas.
                  </p>
                </div>
              ) : (
                <p className="page-section-hint">
                  No template assigned. Create or edit templates under RA Bill Templates, then assign one to this department.
                </p>
              )}

              {editing?.id ? (
                <>
                  <h3 className="page-section-title">Project template overrides</h3>
                  <p className="page-section-hint">
                    Optional. Leave as department default, or assign a different RA Bill Template for a specific project.
                    New RA bills use the project override when set; existing bills stay frozen.
                  </p>
                  {departmentProjects.length === 0 ? (
                    <p className="page-section-hint">No projects mapped to this department yet.</p>
                  ) : (
                    <div className="table-container" style={{ marginBottom: 16 }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Code</th>
                            <th>RA Bill Template</th>
                          </tr>
                        </thead>
                        <tbody>
                          {departmentProjects.map((project) => {
                            const overrideId = projectTemplateOverrides[project.id] ?? '';
                            const inherits = !overrideId;
                            return (
                              <tr key={project.id}>
                                <td style={{ fontWeight: 600 }}>{project.name}</td>
                                <td>{project.code || '—'}</td>
                                <td>
                                  <select
                                    value={overrideId}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setProjectTemplateOverrides((prev) => ({
                                        ...prev,
                                        [project.id]: value,
                                      }));
                                    }}
                                    style={{ minWidth: 260 }}
                                  >
                                    <option value="">
                                      {selectedTemplate
                                        ? `Department default (${selectedTemplate.name})`
                                        : 'None (use department default)'}
                                    </option>
                                    {templates.map((row) => (
                                      <option key={row.id} value={String(row.id)}>
                                        {templateOptionLabel(row)}
                                      </option>
                                    ))}
                                  </select>
                                  {inherits ? (
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>Inherited</span>
                                  ) : (
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#166534' }}>Override</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : null}

              <h3 className="page-section-title">RA Rules by Financial Year</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Financial Year (e.g. 2026-27)</label>
                  <input value={ruleForm.financial_year} onChange={(e) => setRuleForm((prev) => ({ ...prev, financial_year: e.target.value }))} placeholder="2026-27" />
                </div>
                <div className="form-group">
                  <label>Security Deposit %</label>
                  <input type="number" min="0" step="0.01" value={ruleForm.security_deposit_pct} onChange={(e) => setRuleForm((prev) => ({ ...prev, security_deposit_pct: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>GST %</label>
                  <input type="number" min="0" step="0.01" value={ruleForm.gst_pct} onChange={(e) => setRuleForm((prev) => ({ ...prev, gst_pct: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Income Tax %</label>
                  <input type="number" min="0" step="0.01" value={ruleForm.income_tax_pct} onChange={(e) => setRuleForm((prev) => ({ ...prev, income_tax_pct: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Labour Cess %</label>
                  <input type="number" min="0" step="0.01" value={ruleForm.labour_cess_pct} onChange={(e) => setRuleForm((prev) => ({ ...prev, labour_cess_pct: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Taxable Base Method</label>
                  <select value={ruleForm.taxable_base_method} onChange={(e) => setRuleForm((prev) => ({ ...prev, taxable_base_method: e.target.value }))}>
                    <option value="CPWD_5B">CPWD 5(b)</option>
                    <option value="CPWD_5A">CPWD 5(a)</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Recovery Method</label>
                  <select value={ruleForm.recovery_method} onChange={(e) => setRuleForm((prev) => ({ ...prev, recovery_method: e.target.value }))}>
                    <option value="CPWD_FIXED">CPWD Fixed</option>
                    <option value="PERCENTAGE">Percentage Based</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: '1 1 320px' }}>
                  <label>Extra Fields (comma-separated)</label>
                  <input value={ruleForm.extra_fields} onChange={(e) => setRuleForm((prev) => ({ ...prev, extra_fields: e.target.value }))} placeholder="taxable_amount, retention_override" />
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <button type="button" className="btn-secondary btn-sm" onClick={handleAddOrUpdateRule}>Add / Update FY Rule</button>
              </div>
              <div className="table-container" style={{ marginBottom: 10 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>FY</th>
                      <th>SD%</th>
                      <th>GST%</th>
                      <th>IT%</th>
                      <th>Cess%</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: 16, color: '#6b7280' }}>No FY rules added.</td></tr>
                    ) : rules.map((item) => (
                      <tr key={item.financial_year}>
                        <td>{formatFinancialYearLabelIN(item.financial_year)}</td>
                        <td>{item.security_deposit_pct}</td>
                        <td>{item.gst_pct}</td>
                        <td>{item.income_tax_pct}</td>
                        <td>{item.labour_cess_pct}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => {
                              setRuleForm({ ...item });
                              setRules((prev) => prev.filter((x) => x.financial_year !== item.financial_year));
                            }}
                          >
                            Edit
                          </button>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => setRules((prev) => prev.filter((x) => x.financial_year !== item.financial_year))} style={{ marginLeft: 6 }}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                {editing && <button className="btn-secondary" onClick={handleDelete} style={{ color: '#dc2626', borderColor: '#dc2626' }}>Remove</button>}
                <button className="btn-primary" onClick={handleSave}>{editing ? 'Update' : 'Create'}</button>
              </div>
          </div>
        </div>
      ) : (
        <div className="panel page-hint-panel">
          <p className="page-section-hint" style={{ margin: 0 }}>
            Click a department row to edit master data and assign an RA Bill Template. To change template fields or formulas, use <strong>RA Bill Templates</strong>.
          </p>
        </div>
      )}
    </div>
  );
};

export default Departments;
